import { z } from "zod";
import { STAGES } from "./stages.js";
import { PRIORITIES } from "./urgency.js";
import { LAYOUT } from "./layout.js";
/**
 * A client's status is free text, so this only bounds it. Blank means "no
 * status" rather than an empty label, so it normalises to null — the database's
 * check constraint refuses a present-but-blank value outright.
 */
const clientStatus = z
    .string()
    .trim()
    .max(40)
    .transform((value) => (value === "" ? null : value));
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");
const clientNameColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "expected a six-digit colour");
export const createClientSchema = z.object({
    name: z.string().trim().min(1).max(80),
    slack_channel_id: z.string().trim().max(32).nullish(),
    status: clientStatus.nullish(),
});
export const updateClientSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    slack_channel_id: z.string().trim().max(32).nullable(),
    status: clientStatus.nullable(),
    is_private: z.boolean(),
    name_color: clientNameColor,
    canvas_x: z.number().finite(),
    canvas_y: z.number().finite(),
    width: z.number().min(LAYOUT.zone.minWidth).max(LAYOUT.zone.maxWidth),
    height: z.number().min(LAYOUT.zone.minHeight).max(LAYOUT.zone.maxHeight),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, "no fields to update");
/**
 * A logo arrives as a data URL. The shape is checked here; the mime allowlist
 * and the byte cap are enforced by the route, after decoding, because the
 * declared type and the actual payload are both caller-controlled.
 */
/**
 * Who may see a private board, replacing whatever the list held before. Sending
 * the whole set rather than adds and removes means two admins editing at once
 * cannot interleave into a list neither of them chose.
 */
export const clientAccessSchema = z.object({
    user_ids: z.array(z.string().uuid()).max(500),
});
export const clientLogoSchema = z.object({
    data: z.string().min(32).max(400_000),
});
export const createTaskSchema = z.object({
    client_id: z.string().uuid().nullish(),
    worklist_id: z.string().uuid().nullish(),
    title: z.string().trim().min(1).max(200),
    assignee_id: z.string().uuid().nullish(),
    due_date: isoDate.nullish(),
    stage: z.enum(STAGES).default("planning"),
    priority: z.enum(PRIORITIES).default("normal"),
}).refine((value) => Number(Boolean(value.client_id)) + Number(Boolean(value.worklist_id)) === 1, "choose exactly one task home");
export const createSubtaskSchema = z.object({
    task_id: z.string().uuid(),
    title: z.string().trim().min(1).max(200),
    position: z.number().int().min(0).max(9999).default(0),
});
export const updateSubtaskSchema = z
    .object({
    title: z.string().trim().min(1).max(200),
    done: z.boolean(),
    position: z.number().int().min(0).max(9999),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, "no fields to update");
export const createCommentSchema = z.object({
    task_id: z.string().uuid(),
    body: z.string().trim().min(1).max(4000),
});
export const updateCommentSchema = z.object({
    body: z.string().trim().min(1).max(4000),
});
export const updateTaskSchema = z
    .object({
    title: z.string().trim().min(1).max(200),
    assignee_id: z.string().uuid().nullable(),
    due_date: isoDate.nullable(),
    stage: z.enum(STAGES),
    priority: z.enum(PRIORITIES),
    client_id: z.string().uuid(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, "no fields to update");
export const createWorklistSchema = z.object({
    name: z.string().trim().min(1).max(80),
});
export const updateWorklistSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    canvas_x: z.number().finite(),
    canvas_y: z.number().finite(),
    width: z.number().min(LAYOUT.zone.minWidth).max(LAYOUT.zone.maxWidth),
    height: z.number().min(LAYOUT.zone.minHeight).max(LAYOUT.zone.maxHeight),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, "no fields to update");
export const createTaskWorklistSchema = z.object({
    task_id: z.string().uuid(),
    worklist_id: z.string().uuid(),
    stage: z.enum(STAGES),
});
export const createAnnotationSchema = z.object({
    client_id: z.string().uuid().nullish(),
    type: z.enum(["freehand", "shape"]).default("freehand"),
    path_data: z.object({
        points: z.array(z.tuple([z.number().finite(), z.number().finite()])).min(1).max(5000),
    }),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    width: z.number().min(1).max(48),
});
export const updateImageSchema = z
    .object({
    canvas_x: z.number().finite(),
    canvas_y: z.number().finite(),
    scale: z.number().min(0.05).max(8),
    // Dragging a file onto a different column re-anchors it, and dragging it
    // off every column sets it loose.
    client_id: z.string().uuid().nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, "no fields to update");
export const createAttachmentSchema = z.object({
    // Null means the file was put down outside every column. It then belongs to
    // the board rather than to a client — see migration 017.
    client_id: z.string().uuid().nullable(),
    name: z.string().trim().min(1).max(240),
    mime: z.string().trim().min(1).max(120),
    data: z.string().max(28_000_000),
    width: z.number().int().min(1).max(8000).default(320),
    height: z.number().int().min(1).max(8000).default(180),
    canvas_x: z.number().finite(),
    canvas_y: z.number().finite(),
});
export const allowlistEntrySchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    role: z.enum(["admin", "member"]).default("member"),
});
export const updateUserSchema = z.object({
    role: z.enum(["admin", "member"]).optional(),
    slack_user_id: z.string().trim().max(32).nullable().optional(),
});
export const createBoardTextSchema = z.object({
    client_id: z.string().uuid().nullish(),
    text: z.string().trim().min(1).max(1000),
    canvas_x: z.number().finite(),
    canvas_y: z.number().finite(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#23262a"),
    font_size: z.number().min(10).max(96).default(22),
});
export const updateBoardTextSchema = createBoardTextSchema
    .omit({ client_id: true })
    .partial()
    .refine((value) => Object.keys(value).length > 0, "no fields to update");
export const inviteUserSchema = allowlistEntrySchema;
export const updateTeamUserSchema = z
    .object({
    role: z.enum(["admin", "member"]).optional(),
    disabled: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, "no fields to update");
export const recycleKindSchema = z.enum([
    "client", "task", "subtask", "comment", "text", "attachment",
]);
//# sourceMappingURL=schemas.js.map